import { IonModal } from "@ionic/react";
import { useFormik } from "formik";
import { CSSProperties, FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TextField } from "../../components/atoms/LoginTextField";
import {
  ModalActions,
  ModalCard,
  ModalError,
  ModalFields,
  ModalOverlay,
  ModalPrimaryButton,
  ModalQuietButton,
  ModalTitle,
} from "../../components/atoms/ModalKit";
import * as Yup from "yup";
import { changePassword } from "../../services/user.service";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import Icon from "../../components/atoms/Icon";
import { alertCircle } from "ionicons/icons";
import useToast from "../../hooks/useToast";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
}

interface changePasswordFormType {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const changePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required(),
  newPassword: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])(?!.*\s).{8,100}$/,
      "form_error_email_password"
    )
    .required(),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "form_error_email_confirm_password")
    .required(),
});

const ChangePwdModal: FC<Props> = ({ isOpen, onDismiss }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { study } = useConfig();
  const [isError, setIsError] = useState(false);
  const [isOldPasswordFieldUnlocked, setIsOldPasswordFieldUnlocked] =
    useState(false);
  const [isNewPasswordFieldUnlocked, setIsNewPasswordFieldUnlocked] =
    useState(false);
  const [isConfirmPasswordFieldUnlocked, setIsConfirmPasswordFieldUnlocked] =
    useState(false);
  const present = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setIsOldPasswordFieldUnlocked(false);
    setIsNewPasswordFieldUnlocked(false);
    setIsConfirmPasswordFieldUnlocked(false);
  }, [isOpen]);

  const handleSubmit = async (values: changePasswordFormType) => {
    try {
      setIsError(false);
      await changePassword(
        {
          studyId: study.id,
        },
        {
          login: user.attributes.email,
          old_password: values.oldPassword,
          new_password: values.newPassword,
        }
      );
      onDismiss();
    } catch (e) {
      setIsError(true);
      present(e?.response?.data?.message);
    }
  };

  const { values, touched, errors, submitForm, setFieldValue } =
    useFormik<changePasswordFormType>({
      initialValues: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      onSubmit: handleSubmit,
      validationSchema: changePasswordSchema,
    });

  return (
    <IonModal isOpen={isOpen} backdropDismiss={true} onWillDismiss={onDismiss}>
      <ModalOverlay onClick={onDismiss}>
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>{t("change_pwd_title")}</ModalTitle>

          <ModalFields>
            <TextField
              value={values.oldPassword}
              type="text"
              error={touched.oldPassword && errors.oldPassword}
              onChange={(value) => setFieldValue("oldPassword", value)}
              label={t("change_pwd_old_password")}
              placeholder={t("change_pwd_old_password")}
              autoComplete="off"
              inputProps={{
                name: "profile-old-password-value",
                readOnly: !isOldPasswordFieldUnlocked,
                onFocus: () => setIsOldPasswordFieldUnlocked(true),
                onPointerDown: () => setIsOldPasswordFieldUnlocked(true),
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                style: { WebkitTextSecurity: "disc" } as CSSProperties,
                "data-lpignore": "true",
                "data-1p-ignore": "true",
                "data-bwignore": "true",
                "data-form-type": "other",
              }}
            />

            <TextField
              value={values.newPassword}
              type="text"
              error={touched.newPassword && errors.newPassword}
              onChange={(value) => setFieldValue("newPassword", value)}
              label={t("change_pwd_new_password")}
              placeholder={t("change_pwd_new_password")}
              autoComplete="off"
              inputProps={{
                name: "profile-new-password-value",
                readOnly: !isNewPasswordFieldUnlocked,
                onFocus: () => setIsNewPasswordFieldUnlocked(true),
                onPointerDown: () => setIsNewPasswordFieldUnlocked(true),
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                style: { WebkitTextSecurity: "disc" } as CSSProperties,
                "data-lpignore": "true",
                "data-1p-ignore": "true",
                "data-bwignore": "true",
                "data-form-type": "other",
              }}
            />

            <TextField
              value={values.confirmPassword}
              type="text"
              error={touched.confirmPassword && errors.confirmPassword}
              onChange={(value) => setFieldValue("confirmPassword", value)}
              label={t("change_pwd_confirm_password")}
              placeholder={t("change_pwd_confirm_password")}
              autoComplete="off"
              inputProps={{
                name: "profile-confirm-password-value",
                readOnly: !isConfirmPasswordFieldUnlocked,
                onFocus: () => setIsConfirmPasswordFieldUnlocked(true),
                onPointerDown: () => setIsConfirmPasswordFieldUnlocked(true),
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                style: { WebkitTextSecurity: "disc" } as CSSProperties,
                "data-lpignore": "true",
                "data-1p-ignore": "true",
                "data-bwignore": "true",
                "data-form-type": "other",
              }}
            />
          </ModalFields>

          {isError && (
            <ErrorRow>
              <Icon icon={alertCircle} />
              {t("change_pwd_error")}
            </ErrorRow>
          )}

          <ModalActions>
            <ModalQuietButton type="button" onClick={onDismiss}>
              {t("common_cancel")}
            </ModalQuietButton>
            <ModalPrimaryButton type="button" onClick={submitForm}>
              {t("common_next")}
            </ModalPrimaryButton>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </IonModal>
  );
};

/** Kit error line, plus the icon row layout this modal needs. */
const ErrorRow = styled(ModalError)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 20px 0 0;
`;

export default ChangePwdModal;
